import AppStoreContext from 'contexts/AppStoreContext';
import * as React from 'react';
import { ProjectGroup } from 'records/ProjectGroup';
import { List, Map } from 'immutable';
import { BatchProjectGroupsEdit } from 'records/BatchProjectGroupsEdit';
import { User } from 'records/User';

export type GroupManagerValue = {
  fetchAndSetProjectGroups: () => Promise<void>;
  getProjectGroups: () => List<ProjectGroup>;
  createProjectGroup: (name: string) => Promise<void>;
  batchEditProjectGroups: (input: BatchProjectGroupsEdit) => Promise<void>;
  findGroupByName: (name: string) => ProjectGroup | undefined;
  groupsByMember: () => Map<string, List<ProjectGroup>>;
  findGroupById: (id: string) => ProjectGroup | undefined;
  findGroupsByUser: (name: User) => List<ProjectGroup> | undefined;
  editProjectGroup: (
    projectid: string,
    groupid: string,
    name: string
  ) => Promise<void>;
  deleteProjectGroup: (projectid: string, groupid: string) => Promise<void>;
};

const GroupManagerContext = React.createContext<GroupManagerValue | null>(null);

export const GroupManagerProvider = GroupManagerContext.Provider;

class GroupManager {
  constructor(
    public fetchAndSetProjectGroups: () => Promise<void>,
    public getProjectGroups: () => List<ProjectGroup>,
    public createProjectGroup: (name: string) => Promise<void>,
    public batchEditProjectGroups: (
      input: BatchProjectGroupsEdit
    ) => Promise<void>,
    public groupsByMember: () => Map<string, List<ProjectGroup>>,
    public editProjectGroup: (
      projectid: string,
      groupid: string,
      name: string
    ) => Promise<void>,
    public deleteProjectGroup: (
      projectid: string,
      groupid: string
    ) => Promise<void>
  ) {}

  findGroupByName(name: string) {
    return this.getProjectGroups().find((group) => group.name === name);
  }

  findGroupById(id: string) {
    return this.getProjectGroups().find((group) => group.groupid === id);
  }

  findGroupsByUser(user: User) {
    const groupsByMembers = this.groupsByMember();
    return groupsByMembers.get(user.id);
  }
}

// wraps an async function, retains its parameters, but returns Promise<void>
function voidResponse<T extends Array<any>>(fn: (...args: T) => Promise<any>) {
  return async (...args: T) => {
    await fn(...args);
  };
}

export const AppGroupManagerProvider: React.FC = ({ children }) => {
  const { actions: appActions, getState } = React.useContext(AppStoreContext);

  const groupManager = new GroupManager(
    voidResponse(appActions.getProjectGroups),
    () => getState().projectGroups,
    voidResponse(appActions.createProjectGroup),
    voidResponse(appActions.batchEditProjectGroups),
    () => getState().groupsByMember,
    voidResponse(appActions.editProjectGroup),
    voidResponse(appActions.deleteProjectGroup)
  );

  return (
    <GroupManagerProvider value={groupManager}>{children}</GroupManagerProvider>
  );
};

export const useGroupManager = (): GroupManagerValue => {
  const groupManager = React.useContext(GroupManagerContext);

  if (groupManager === null) {
    throw new Error(
      'useGroupManager must be used within a GroupManagerProvider'
    );
  }

  return groupManager;
};
